import { Helmet } from 'react-helmet-async'
import { Income, IncomeLookup, IncomeProvider, useIncome } from "./IncomeProvider";
import { useFilteredCommodities } from "src/filter/FilteredCommodities";
import { useTranslation } from "react-i18next";
import _ from 'lodash';
import { commodityUrl, useProductMapping } from 'src/products/products';
import { useRoles } from 'src/user/Auth';
import { does_patent_cover_income } from '.';
import { useSingleTeamMangement } from 'src/Management';
import { useFxContext } from 'src/forecast/FxProvider';
import { Link } from 'src/navigation';
import { memberUrl } from 'src/patents/utils';
import clsx from 'clsx';
import { TableCopyButton } from 'src/components/button/TableCopyButton';
import { useLocalState } from 'src/settings/localStorage';
import ToggleButton from 'src/components/ToggleButton';
import { ArrowDownIcon, ArrowsUpDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';

export default function IncomeOverviewPage() {
  return (
    <IncomeProvider>
      <IncomeOverview />
    </IncomeProvider>
  )
}

const currencyFormat = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const availableSortFields = [
  'commodityClass',
  'commodityReference',
  'country',
  'internalReference',
]

function IncomeOverview() {
  const {t} = useTranslation()
  const {hasClaimScopes} = useRoles()

  const { team } = useSingleTeamMangement()
  const currency = team?.currency ?? 'EUR'
  const { fxConverter } = useFxContext()

  const [includingUnprotected, setIncludingUnprotected] = useLocalState('income-overview-include-unprotected', false)
  const [includeIncomelessProducts , setIncludeIncomelessProducts ] = useLocalState("income-overview-include-incomeless", false)
  const [onlyGranted, setOnlyGranted] = useLocalState("income-overview-only-granted", false)
  const [sortField, setSortField] = useLocalState("income-overview-sort-field", availableSortFields[0])
  const [sortOrder, setSortOrder] = useLocalState("income-overview-sort-order", +1)

  const {incomeLookup, years: _years}: {years: number[], incomeLookup: IncomeLookup, incomes: Income[]} = useIncome()
  const { commodities } = useFilteredCommodities()
  const {membersByCommodityId, claimScopeMembersByCommodityId: _claimScopeMembersByCommodityId} = useProductMapping()
  const claimScopeMembersByCommodityId = hasClaimScopes ? _claimScopeMembersByCommodityId : {}
  const years = _(_years).sortBy(y => -y).value()

  const rows = _(commodities)
    .flatMap(commodity => {
      const incomeForCommodity = incomeLookup[commodity.commodityId] ?? {}
      if (includeIncomelessProducts && _.isEmpty(incomeForCommodity)) {
        if (includingUnprotected) {
          return [{
            ...commodity,
            country: '',
            coveringMembers: [],
            incomes: years.map(() => null)
          }]
        } else {
        const members = _([
          ...(membersByCommodityId[commodity.commodityId] ?? []),
          ...(claimScopeMembersByCommodityId[commodity.commodityId] ?? []),
        ])
          .filter(m => m.familyMemberStatus === 'granted' || !onlyGranted)
          .size()

          if (members > 0) {
            return [{
              ...commodity,
              country: '',
              coveringMembers: [],
              incomes: years.map(() => null)
            }]
          } else {
            return []
          }
        }
      }
      const countries = _.keys(incomeForCommodity)
      const members = _([
        ...(membersByCommodityId[commodity.commodityId] ?? []),
        ...(claimScopeMembersByCommodityId[commodity.commodityId] ?? []),
      ])
        .filter(m => m.familyMemberStatus === 'granted' || !onlyGranted)
        .uniqBy(m => m.familyMemberId)
        // .filter(m => countries.some(c => does_patent_cover_income(m, c)))
        .value()
      // console.log({incomeForCommodity})
      return countries.map(incomeCountry => {
        const ics = incomeForCommodity[incomeCountry] 
        const coveringMembers = members.filter(m => does_patent_cover_income(m, incomeCountry))
        return {
          ...commodity,
          country: incomeCountry,
          coveringMembers,
          incomes: years
            .map(y => ics?.[y])
            .map(income => income?.amount ? {...income, inTeamCcy: fxConverter.convert(income.amount, income.currency, currency)} : null)
        }
      }).filter(({coveringMembers}) => includingUnprotected || coveringMembers.length > 0)
    })
    .sortBy(row => sortField === 'internalReference' ? (row.coveringMembers[0]?.internalReference ?? '') : row[sortField])
    .thru(sorted => sortOrder === -1 ? sorted.reverse() : sorted)
    .value()
  // console.log({rows})
  //console.log({years})

  function clickHeader(field: string) {
    if (field === sortField) {
      setSortOrder(-sortOrder)
    } else {
      setSortField(field)
      setSortOrder(1)
    }
  }

  const totals = rows.reduce((acc, row) => {
    row.incomes.forEach(income => {
      if (income) {
        acc[income.year] = acc[income.year] + income.inTeamCcy
      }
    });
    return acc;
  }, _(years).map(y => [y, 0.0]).fromPairs().value())

  const headers = [
    'commodityClass',
    'commodityReference',
    'country',
    'internalReference',
    ...years.map(y => `income-${y}`),
  ]
  const header_nice = _(headers)
    .map(h => [h, h.startsWith('income-') ? `${t('income')} ${h.replace('income-', '')}` : t(h)])
    .fromPairs()
    .value()

  return (
    <>
      {/* @ts-ignore */}
      <Helmet>
        <title>{t('product-portfolio')} | Patent Cockpit</title>
      </Helmet>
      <div className="modern-header-row">
        <h2 className="modern-h2">Income Overview</h2>
      </div>
      <div className="px-4">
        <div className="pb-2 flex flex-row gap-6 items-center">
          <TableCopyButton tableId="income-table" className="btn-secondary text-xs">
            Copy to Clipboard
          </TableCopyButton>
          <label className="flex flex-row gap-2 items-center">
            <ToggleButton setChecked={setIncludingUnprotected} checked={includingUnprotected} />
            <span className="label inline-block">Include Unprotected</span>
          </label>
          <label className="flex flex-row gap-2 items-center">
            <ToggleButton setChecked={setIncludeIncomelessProducts} checked={includeIncomelessProducts} />
            <span className="label inline-block">Include Products without Income</span>
          </label>
          <label className="flex flex-row gap-2 items-center">
            <ToggleButton setChecked={setOnlyGranted} checked={onlyGranted} />
            <span className="label inline-block">Include Only Granted Patents</span>
          </label>
        </div>
        <table id="income-table">
          <thead>
            <tr className="pc-header-row">
              {headers.map(h => (
                <th key={h} className={clsx('pc-header-cell', h.startsWith('income-') && 'text-right')}>
                   {header_nice[h]} { availableSortFields.includes(h) && <SortButton onClick={() => clickHeader(h)} sortOrder={sortOrder} isActive={sortField === h} /> }
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index} className="pc-data-row">
                <td className="pc-cell">{row.commodityClass}</td>
                <td className="pc-cell"><Link className='underline-link' to={commodityUrl(row)}>{row.commodityReference}</Link></td>
                <td className="pc-cell">{row.country}</td>
                <td className="pc-cell space-x-1">
                  {row.coveringMembers.map((member, mi) => (
                    <Link key={mi} to={memberUrl(member)} className="underline-link after:content-[','] last:after:content-['']">
                      {member.internalReference}{onlyGranted ? '' : ` (${t(member.familyMemberStatus)})`}
                    </Link>
                  ))}
                </td>
                <>
                  {row.incomes.map((income, index) => (
                    <td key={index} className="pc-cell text-right tabular-nums">
                      {income?.inTeamCcy ? currencyFormat.format(income?.inTeamCcy) : null}
                    </td>
                  ))}
                </>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="pc-footer-row">
              <td className="pc-cell font-bold">Totals</td>
              {_.range(_.size(headers.filter(h => !h.startsWith('income-'))) - 1).map(i => (
                <td key={i} className="pc-cell"></td>
              ))}
              <>
                {years.map((year, index) => (
                  <td key={index} className="pc-cell text-right tabular-nums">
                    {currencyFormat.format(totals[year])}
                  </td>
                ))}
              </>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  )
}

function SortButton({ onClick, sortOrder, isActive }) {
  return (
    <button onClick={onClick} className="text-pcx-700">
      {!isActive ?
      <ArrowsUpDownIcon className={buttonStyle} /> 
      : sortOrder === 1 
      ? <ArrowDownIcon className={buttonStyle} /> 
      : <ArrowUpIcon className={buttonStyle} />}
    </button>
  )
}

const buttonStyle = 'inline-block size-4'